/**
 * Testimonials Block — centre-focused Splide with progress bar and counter.
 *
 * @package UnderstrapChild
 */

import Splide from '@splidejs/splide';

// ACF's block-preview HTML injection (needed here because this block's markup
// includes inline SVG) can leave injected elements bound to a stale document
// realm, which silently breaks Splide's internal `instanceof HTMLElement`
// checks (used when it filters slide/list children) even though the DOM
// looks completely normal. Recursively rebuild any foreign-realm element (and
// its descendants) in the correct realm so Splide can find and size them.
function repairSplideRealm( splideEl ) {
	var doc = splideEl.ownerDocument;
	var win = doc.defaultView || window;

	function isForeign( node ) {
		try {
			var ns = node.namespaceURI;
			if ( ns === 'http://www.w3.org/2000/svg' ) {
				return ! ( node instanceof win.SVGElement );
			}
			if ( ! ns || ns === 'http://www.w3.org/1999/xhtml' ) {
				return ! ( node instanceof win.HTMLElement );
			}
			return ! ( node instanceof win.Element );
		} catch ( e ) {
			return true;
		}
	}

	function fix( node ) {
		var children = Array.prototype.slice.call( node.children );
		if ( ! isForeign( node ) ) {
			children.forEach( fix );
			return node;
		}

		var fresh = node.namespaceURI === 'http://www.w3.org/2000/svg'
			? doc.createElementNS( node.namespaceURI, node.tagName.toLowerCase() )
			: doc.createElement( node.tagName.toLowerCase() );
		Array.prototype.slice.call( node.attributes ).forEach( function ( attr ) {
			fresh.setAttribute( attr.name, attr.value );
		} );
		while ( node.firstChild ) {
			var child = node.firstChild;
			fresh.appendChild( child.nodeType === 1 ? fix( child ) : child );
		}
		node.replaceWith( fresh );
		return fresh;
	}

	var track = splideEl.querySelector( '.splide__track' );
	if ( track ) fix( track );
}

function initTestimonialsBlock( root ) {
	if ( ! root ) return;

	var el = root.querySelector( '[data-testimonials-splide]' );
	if ( ! el ) return;
	// Guard against the editor preview swapping in a stale/partial DOM mid-render.
	if ( ! el.querySelector( '.splide__track' ) ) return;

	repairSplideRealm( el );

	// Destroy any previous instance so the editor preview can re-mount cleanly.
	if ( el._splide ) {
		el._splide.destroy();
	}

	// Count real (non-clone) slides before initialising so we can disable loop
	// mode when there is only one testimonial — loop clones would otherwise show
	// the same slide duplicated on both sides.
	var slideCount = el.querySelectorAll( '.splide__slide' ).length;
	var isSingle   = slideCount === 1;

	// Splide can throw if the editor preview re-renders mid-mount; don't let
	// that crash the block preview and blank out its styling.
	try {

	var splide = new Splide( el, isSingle ? {
		type       : 'slide',
		perPage    : 1,
		focus      : 'center',
		padding    : { left: 'calc(50% - 305px)', right: 'calc(50% - 305px)' },
		gap        : '32px',
		arrows     : false,
		pagination : false,
		drag       : false,
		trimSpace  : false,
	} : {
		type             : 'loop',
		perPage          : 1,
		focus            : 'center',
		padding          : { left: 'calc(50% - 305px)', right: 'calc(50% - 305px)' },
		gap              : '32px',
		arrows           : false,
		pagination       : false,
		speed            : 380,
		easing           : 'cubic-bezier(0.16, 1, 0.3, 1)',
		drag             : true,
		flickPower       : 600,
		flickMaxPages    : 1,
		dragMinThreshold : { mouse: 4, touch: 10 },
	} );

	var fill    = root.querySelector( '.testimonials-block__progress-fill' );
	var counter = root.querySelector( '.testimonials-block__counter' );
	var total   = el.querySelectorAll( '.splide__slide:not(.splide__slide--clone)' ).length;

	function updateIndicator( index ) {
		var current = ( ( index % total ) + total ) % total;
		if ( fill ) {
			fill.style.width = ( ( current + 1 ) / total * 100 ) + '%';
		}
		if ( counter ) {
			var n = String( current + 1 ).padStart( 2, '0' );
			var t = String( total ).padStart( 2, '0' );
			counter.textContent = n + '/' + t;
		}
	}

	// During a loop transition Splide animates a *clone* of the target slide
	// into centre. Mark those clones as incoming so they show at full opacity
	// (matching the real slide). The class is removed once the transition ends
	// and Splide has already swapped the real slide back into position.
	var realSlides = Array.from( el.querySelectorAll( '.splide__slide:not(.splide__slide--clone)' ) );

	function markIncomingClones( newIndex ) {
		var idx   = ( ( newIndex % total ) + total ) % total;
		var label = realSlides[ idx ] ? realSlides[ idx ].getAttribute( 'aria-label' ) : null;
		el.querySelectorAll( '.splide__slide--clone' ).forEach( function ( clone ) {
			clone.classList.toggle( 'is-incoming-clone', !! label && clone.getAttribute( 'aria-label' ) === label );
		} );
	}

	function clearIncomingClones() {
		el.querySelectorAll( '.splide__slide--clone.is-incoming-clone' ).forEach( function ( clone ) {
			clone.classList.remove( 'is-incoming-clone' );
		} );
	}

	splide.on( 'move', function ( newIndex ) {
		markIncomingClones( newIndex );
	} );

	splide.on( 'moved', function ( newIndex ) {
		clearIncomingClones();
		updateIndicator( newIndex );
	} );

	// Prev / Next buttons.
	var prevBtn = root.querySelector( '.js-slider-prev' );
	var nextBtn = root.querySelector( '.js-slider-next' );
	if ( prevBtn ) { prevBtn.addEventListener( 'click', function () { splide.go( '<' ); } ); }
	if ( nextBtn ) { nextBtn.addEventListener( 'click', function () { splide.go( '>' ); } ); }

	el._splide = splide;

	splide.mount();
	updateIndicator( 0 );

	} catch ( err ) {
		if ( window.console && console.warn ) {
			console.warn( '[testimonials-block] Splide init failed, preview left static:', err );
		}
	}
}

// ── Frontend ──────────────────────────────────────────────────────────────────
document.addEventListener( 'DOMContentLoaded', function () {
	document.querySelectorAll( '.testimonials-block' ).forEach( function ( block ) {
		initTestimonialsBlock( block );
	} );
} );

// ── Block editor preview (ACF) ────────────────────────────────────────────────
if ( typeof acf !== 'undefined' ) {
	acf.addAction( 'render_block_preview/type=testimonials-block', function ( $el ) {
		var root = ( $el && $el[0] ) ? $el[0].querySelector( '.testimonials-block' ) : null;
		initTestimonialsBlock( root );
	} );
}
